import type { de } from './de';

/** English texts, with the same keys as the German ones. */
export const en: typeof de = {
  name: 'Customers',
  description:
    'Customers, prospects, planners, suppliers and authorities of the company with master data, blocks and VAT check.',
  nav: { organizations: 'Organisations', contacts: 'Contacts' },
  permissions: {
    organization: {
      read: {
        label: 'View organisations',
        description: 'Open the list and details of organisations, read their history.',
      },
      write: {
        label: 'Edit organisations',
        description: 'Create and change organisations, check VAT IDs.',
      },
      comment: {
        label: 'Comment on organisations',
        description: 'Write comments on organisations.',
      },
      remind: {
        label: 'Reminders on organisations',
        description: 'Set own reminders on organisations.',
      },
      export: {
        label: 'Export organisations',
        description: 'Download the list of organisations as a CSV file.',
      },
      archive: {
        label: 'Archive and block organisations',
        description: 'Archive, restore, block and unblock organisations.',
      },
      merge: {
        label: 'Merge duplicates',
        description: 'Merge one organisation into another; comments and reminders move along.',
      },
    },
    contact: {
      read: {
        label: 'View contacts',
        description: 'Open the list and details of contacts, read their history.',
      },
      write: {
        label: 'Edit contacts',
        description:
          'Create and change contacts, set the main contact, mark contacts as left. With scope "own" only contacts created by the same person.',
      },
      comment: {
        label: 'Comment on contacts',
        description: 'Write comments on contacts.',
      },
      remind: {
        label: 'Reminders on contacts',
        description: 'Set own reminders on contacts.',
      },
      export: {
        label: 'Export contacts',
        description: 'Download the list of contacts as a CSV file.',
      },
      delete: {
        label: 'Delete contacts',
        description:
          'Delete contacts for good, e.g. ones created by mistake or twice. Name, contact details and comments are lost.',
      },
    },
    settings: {
      manage: {
        label: 'Manage customer settings',
        description: 'Change the settings of the Customers module for the company.',
      },
    },
  },
  roles: {
    reader: 'Customers: Read',
    editor: 'Customers: Edit',
    manager: 'Customers: Manage',
  },
  audit: {
    organization: {
      create: 'Organisation created',
      update: 'Organisation changed',
      archive: 'Organisation archived',
      restore: 'Organisation restored',
      block: 'Organisation blocked',
      unblock: 'Organisation unblocked',
      'vat-check': 'VAT ID checked',
      merge: 'Duplicate merged',
    },
    address: {
      create: 'Address added',
      update: 'Address changed',
      delete: 'Address deleted',
    },
    contact: {
      create: 'Contact created',
      update: 'Contact changed',
      primary: 'Main contact changed',
      leave: 'Contact left',
      return: 'Contact current again',
      delete: 'Contact deleted',
    },
    list: {
      create: 'Distribution list created',
      update: 'Distribution list changed',
      delete: 'Distribution list deleted',
      'add-members': 'Contacts added to distribution list',
      'remove-member': 'Contact removed from distribution list',
    },
  },
  sequences: { organization: 'Customer number' },
  offline: { organizations: 'Organisations' },
  guides: {
    organizations: {
      title: 'Managing organisations',
      summary: 'Create, avoid duplicates, block, check VAT IDs and set reminders.',
    },
    contacts: {
      title: 'Maintaining contacts',
      summary: 'Create contacts, set the main contact, objection to advertising and leaving.',
    },
  },
  statuses: { active: 'Active', inactive: 'Inactive' },
  fields: {
    number: 'Number',
    name: 'Name',
    shortName: 'Short name',
    status: 'Status',
    industry: 'Industry',
    vatId: 'VAT ID',
    website: 'Website',
    phone: 'Phone',
    email: 'Email',
    paymentTermDays: 'Payment term (days)',
    datevDebtor: 'DATEV debtor',
    accountManager: 'Account manager',
    city: 'City',
    updated: 'Changed',
  },
  organizations: {
    title: 'Organisations',
    description: 'Customers, prospects, planners, suppliers and authorities of the company.',
    new: 'New organisation',
    search: 'Search organisations',
    searchPlaceholder: 'Name, number, phone …',
    allStatuses: 'All statuses',
    archive: 'Archive',
    current: 'Current',
    archived: 'Archived',
    blocked: 'Blocked',
    merged: 'Merged',
    exportName: 'Organisations',
    empty: 'No organisations yet',
    emptyHint: 'Create the first customer with “New organisation”.',
    emptyReadOnly: 'Organisations appear here once created.',
    noMatches: 'No matching organisations',
    noMatchesHint: 'Change the search or filters.',
  },
  organization: {
    masterData: 'Master data',
    masterDataEmpty: 'No contact details, VAT ID or payment terms yet.',
    changed: 'changed {{when}}',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    notFound: 'Organisation not found',
    notFoundHint:
      'The address is wrong, the organisation was deleted or belongs to another company.',
    blockedTitle: 'Blocked: do not deliver',
    archivedTitle: 'Archived',
    archivedSince: 'Since {{when}}. Archived organisations are not offered in pickers.',
    mergedTitle: 'Merged into',
    block: 'Block',
    blockTitle: 'Block organisation',
    blockDescription:
      '“{{name}}” is marked as “do not deliver”. Other modules show the reason before they take an order.',
    blockReason: 'Reason',
    blockedToast: 'Organisation blocked.',
    unblock: 'Unblock',
    unblockTitle: 'Lift the block?',
    unblockDescription: '“{{name}}” can be delivered to again afterwards.',
    unblocked: 'Block lifted.',
    archive: 'Archive',
    archiveTitle: 'Archive organisation?',
    archiveDescription:
      '“{{name}}” disappears from the list and from pickers. References in other modules stay, it can be restored at any time.',
    archivedToast: 'Organisation archived.',
    restore: 'Restore',
    restored: 'Organisation restored.',
    merge: 'Merge',
    mergeTitle: 'Merge with another organisation',
    mergeDescription: 'For duplicates: “{{name}}” is merged into the chosen organisation.',
    mergeTarget: 'Keep',
    mergeWarning:
      '“{{name}}” is archived afterwards. Comments and reminders move to the chosen organisation, references of other modules are switched. This cannot be undone.',
    merged: 'Merged into {{number}}.',
    mergedShares_one: '{{count}} share with other companies was revoked.',
    mergedShares_other: '{{count}} shares with other companies were revoked.',
  },
  form: {
    createTitle: 'New organisation',
    editTitle: 'Edit organisation',
    created: 'Organisation {{number}} created.',
    updated: 'Changes saved.',
    shortNameHint: 'What the organisation is called internally, e.g. in lists.',
    vatIdHint: 'With country code, e.g. DE123456789.',
    reload: 'Reload',
    conflictHint: 'Reload shows the current state. Entries in this dialog are lost.',
  },
  duplicates: {
    title_one: 'Possibly exists already',
    title_other: 'Possibly exists already ({{count}} matches)',
    hint: 'Check before creating. Saving stays possible.',
    reasons: {
      name: 'similar name',
      vatId: 'same VAT ID',
      email: 'same mail domain',
      phone: 'same phone number',
    },
  },
  contacts: {
    title: 'Contacts',
    description: 'People at customers, planners, suppliers and authorities.',
    new: 'New contact',
    search: 'Search contacts',
    searchPlaceholder: 'Name, position, phone …',
    allOrganizations: 'All organisations',
    state: 'State',
    current: 'Current',
    left: 'Left',
    primary: 'Main contact',
    objection: 'Objects to advertising',
    anonymized: 'Anonymised',
    exportName: 'Contacts',
    empty: 'No contacts yet',
    emptyHint: 'Use "New contact" to add the first person.',
    emptyReadOnly: 'Contacts appear here once they are added.',
    noMatches: 'No matching contacts',
    noMatchesHint: 'Change the search or the filters.',
    ofOrganization: 'Contacts of {{name}}',
    emptyOrganization: 'No contacts',
    emptyOrganizationHint: 'Use "New contact" to add a person of this organisation.',
  },
  contact: {
    data: 'Contact details',
    notFound: 'Contact not found',
    notFoundHint: 'The address is wrong, the contact was deleted or belongs to another company.',
    teamsChat: 'Chat in Teams',
    actionsFor: 'Actions for {{name}}',
    makePrimary: 'Make main contact',
    removePrimary: 'No longer main contact',
    primarySet: 'Main contact set.',
    primaryRemoved: 'No longer the main contact.',
    leave: 'Left',
    leaveTitle: 'Mark as left?',
    leaveDescription:
      '{{name}} no longer works at {{organization}}. The entry stays with its history, but is no longer the main contact and is not offered in pickers.',
    leftToast: 'Marked as left.',
    return: 'Current again',
    returned: 'Listed as current again.',
    leftTitle: 'Left',
    leftSince: 'Since {{when}}.',
    objectionTitle: 'Objects to advertising: send no advertising',
    objectionSince: 'Recorded {{when}}. Applies to newsletters, mailings and sales calls.',
    anonymizedTitle: 'Anonymised',
    anonymizedHint:
      'Name and contact details were removed after the retention period. The entry can no longer be changed.',
    delete: 'Delete',
    deleteTitle: 'Delete contact?',
    deleteDescription:
      '{{name}} is deleted with contact details, comments and reminders. This cannot be undone. Entries that refer to the person, such as projects, then show "Not available". If the person only no longer works there, choose "Left" instead.',
    deleted: 'Contact deleted.',
  },
  contactFields: {
    organization: 'Organisation',
    salutation: 'Salutation',
    title: 'Title',
    firstName: 'First name',
    lastName: 'Last name',
    name: 'Name',
    position: 'Position',
    department: 'Department',
    email: 'Email',
    phone: 'Phone',
    mobile: 'Mobile',
    language: 'Language',
    primary: 'Main contact',
    marketingObjection: 'Objects to advertising',
    state: 'State',
  },
  contactForm: {
    createTitle: 'New contact',
    editTitle: 'Edit contact',
    created: 'Contact created.',
    updated: 'Changes saved.',
    primaryHint:
      'First person to contact at the organisation. A previous main contact loses the role.',
    objectionHint: 'The person objected to the use of their data for advertising.',
  },
  contactPicker: {
    placeholder: 'Choose contact',
    search: 'Search by name',
    empty: 'No contact found.',
    create: 'Create contact',
    createNamed: 'Create “{{name}}” as contact',
  },
  salutations: { mr: 'Mr', ms: 'Ms', none: 'No salutation' },
  languages: {
    none: 'Not specified',
    de: 'German',
    en: 'English',
    fr: 'French',
    it: 'Italian',
    nl: 'Dutch',
    pl: 'Polish',
  },
  picker: {
    placeholder: 'Choose organisation',
    search: 'Search name or number',
    empty: 'No organisation found.',
    create: 'Create organisation',
    createNamed: 'Create “{{name}}” as organisation',
    loading: 'Loading …',
    blocked: 'Blocked: {{reason}}',
    unavailable: 'Not available',
  },
  errors: {
    name: 'Enter a name.',
    vatId: 'Enter the VAT ID with country code, e.g. DE123456789.',
    website: 'Enter a valid address, e.g. www.example.com.',
    phone: 'Use only digits, spaces and + ( ) / . -',
    email: 'Enter a valid email address.',
    paymentTermDays: 'Enter a whole number from 0 to 365.',
    datevDebtor: 'Up to 20 letters, digits or hyphens.',
    accountManager: 'Not an active person of the company. Choose someone else.',
    blockReason: 'Enter a reason for the block.',
    countryCode: 'Enter a two-letter country code, e.g. DE.',
    coordinates: 'Coordinates incomplete. Choose the address through the search again.',
    invalid: 'Check this entry.',
    invalidInput: 'Check the entries.',
    noVatId: 'No VAT ID on file.',
    vatNotEu: 'VIES only checks VAT IDs from the EU.',
    anonymized: 'The organisation was anonymised and can no longer be changed.',
    merged: 'The organisation was merged and is read-only now.',
    mergeTarget: 'This organisation cannot be the target: it is archived or merged itself.',
    mergeTargetMissing: 'Choose a target.',
    versionConflict: 'Changed by someone else in the meantime.',
    notFound: 'Organisation not found. It may have been deleted.',
    forbidden: 'A permission is missing for this.',
    rateLimited: 'Too many requests, please try again shortly.',
    lastName: 'Enter a last name.',
    organization: 'Choose an organisation.',
    contactOrganization: 'The organisation no longer exists or was merged. Choose another one.',
    contactAnonymized: 'The contact was anonymised and can no longer be changed.',
    contactLeft: 'A contact who left cannot be the main contact.',
    contactNotFound: 'Contact not found. It may have been deleted.',
  },
  addressKinds: {
    main: 'Head office',
    billing: 'Billing address',
    delivery: 'Delivery address',
    site: 'Construction site',
    other: 'Other',
  },
  addresses: {
    title: 'Addresses',
    add: 'Add address',
    none: 'No address yet.',
    edit: 'Change address',
    remove: 'Delete address',
    removeTitle: 'Delete address?',
    removeDescription: 'The address is removed from the organisation.',
    removed: 'Address deleted.',
    saved: 'Address saved.',
    addTitle: 'Add address',
    editTitle: 'Change address',
    map: 'Map of the addresses of {{name}}',
    search: 'Search address',
    searchHint: 'Fills in the fields below. Without a hit, fill in the fields directly.',
    pointHint:
      'Set by the address search. If it does not fit, e.g. on a large site, move the point on the map or enter coordinates.',
    fields: {
      kind: 'Type',
      label: 'Label',
      street: 'Street',
      houseNumber: 'House number',
      extra: 'Addition',
      postcode: 'Postcode',
      city: 'City',
      countryCode: 'Country (code)',
    },
  },
  map: {
    open: 'Map',
    list: 'List',
    views: 'View',
    title: 'Organisations on the map',
    description:
      'Head offices of current organisations whose address was chosen through the search.',
    empty: 'No organisation on the map',
    emptyHint: 'Head offices appear here once their address was chosen through the address search.',
  },
  import: {
    organizations: 'Import organisations',
    errors: {
      status: 'Unknown status. Allowed: active, inactive.',
    },
  },
};
